/**
 * Tutor API client - Can Do, sessions, progress.
 * Uses gateway (or VITE_AI_TUTOR_BASE_URL when set) for auth forwarding.
 *
 * **Roles:** Student-scoped REST paths are also guarded in `attachAuthInterceptors` (see
 * `src/auth/tutorStudentApiScope.ts`). Teacher flows must use `listTeacherLessons`, `/teacher/*`,
 * `/v1/tutor/progress/analytics/class`, etc. — not `listLessons` or `/students/me/*`.
 * Teacher **class** progress analytics (`getClassProgressAnalytics`) always uses the **gateway** base URL
 * and `/v1/tutor/...` so it matches `teacherTutorAnalyticsApi` (avoids 404 when `VITE_AI_TUTOR_BASE_URL` points at direct ai-tutor).
 */
import { isAxiosError } from 'axios';
import { createHttpClient } from '../../../api/http';
import { normalizeTutorBaseUrl, resolveStudentTutorHttpBase } from '../../../api/tutorHttpBases';
import { serviceUrls } from '../../../config/serviceUrls';
import { normalizeCanDoLessonTypeField } from '../../../constants/lessonCanDoTypes';
import { isTemporaryTutorNetworkError } from '../tutorNetworkErrors';
import { buildCommunicationInstructionFallback } from '../communicationInstructionFallback';
import {
  getApiErrorBackendMessage,
  getApiErrorCode,
  getApiErrorMessage,
  logApiErrorDiagnostics,
} from '../../../utils/apiErrorMessage';
import {
  buildSystemInstructionCacheKey,
  getCachedLessonIndex,
  getCachedSystemInstruction,
  invalidateLessonIndexCache,
  setCachedLessonIndex,
  setCachedSystemInstruction,
} from './tutorBootstrapCache';

function tutorCacheTenantScope(): string {
  if (typeof localStorage === 'undefined') return 'unknown';
  try {
    return String(localStorage.getItem('X_TENANT_ID') || '').trim() || 'unknown';
  } catch {
    return 'unknown';
  }
}

const TUTOR_BASE = resolveStudentTutorHttpBase();
const client = createHttpClient(TUTOR_BASE);
/** Browser-facing Communication Tutor instruction always uses the gateway (CORS + auth). */
const gatewayTutorClient = createHttpClient(serviceUrls.gateway);
const GATEWAY_TUTOR_PREFIX = '/v1/tutor';

/**
 * Gateway BFF mounts tutor routes as `/v1/tutor/*`.
 * The direct ai-tutor FastAPI app mounts them as `/tutor/*` (no `/v1`).
 * Override with `VITE_TUTOR_REST_API_PREFIX=/tutor` or `/v1/tutor` when auto-detection is wrong.
 */
function tutorRestApiPrefix(_baseUrl: string): string {
  const explicit = import.meta.env.VITE_TUTOR_REST_API_PREFIX?.trim();
  if (explicit === '/tutor' || explicit === '/v1/tutor') return explicit;
  // All calls should use the gateway `/v1/tutor` path by default.
  return '/v1/tutor';
}

const PREFIX = tutorRestApiPrefix(TUTOR_BASE);

const TB_BOOK_ID = 'tamil-sec3-g3-u5-tb';

/** Textbook page from tutor API; `image_url` may be site-root path or absolute HTTPS (e.g. GCS). */
export interface TutorPageAsset {
  page_no: number;
  image_url: string;
  gcs_path?: string;
  type?: string;
  book_id?: string;
  source_type?: string;
  printed_page?: number;
  pdf_page_index?: number;
  page_order_within_lesson?: number;
}

export interface TutorSublessonItem {
  sublesson_no: string;
  sublesson_index: number;
  title: string;
  type?: string;
  page_start?: number;
  page_end?: number;
  /** Populated when the backend attaches textbook pages (grades 2–3). */
  page_assets?: TutorPageAsset[];
}

export interface LessonItem {
  lesson_no: number;
  lesson_title: string;
  lesson_type?: string;
  /** RAG `book_id` when present — used to scope Story Studio to the same textbook as the tutor. */
  book_id?: string | null;
  page_start?: number;
  page_end?: number;
  sublesson_count?: number;
  sublessons?: TutorSublessonItem[];
  /** Lesson-wide textbook pages (grades 2–3); sublesson may override with its own `page_assets`. */
  page_assets?: TutorPageAsset[];
  section_key?: string | null;
  topic_ta?: string | null;
  /** Tamil Sec3 G3 Unit 5 — false when source assets are missing (e.g. listening 5.7). */
  available?: boolean;
  source_status?: string;
  unavailable_reason?: string | null;
  activity_id?: string;
  /** Tamil Sec3 G3 Unit 5 — learner-facing lesson topic slug (7-item path). */
  lesson_topic_id?: string;
  /** Nested TG activities under a lesson topic (not shown as root path cards). */
  related_activities?: TamilRelatedActivityItem[];
  show_in_lesson_path?: boolean;
  primary_source?: string;
  source_pages?: number[];
  initial_display_page?: number;
}

export interface TamilRelatedActivityItem {
  activity_id: string;
  sequence_within_lesson: number;
  title_ta: string;
  title_en?: string;
  activity_role?: string;
  tg_ref?: string;
  show_in_lesson_path?: boolean;
  primary_source?: string;
  tb_ref?: string | null;
  ab_ref?: string | null;
  source_pages?: number[];
  initial_display_page?: number | null;
  available?: boolean;
  source_status?: string;
  unavailable_reason?: string | null;
}

export type LessonIndexItem = LessonItem;
export interface TutorSublessonProgressDTO {
  sublesson_no: string;
  title?: string;
  type?: string;
  chunk_total: number;
  chunks_completed: number;
  percent: number;
  status?: 'not_started' | 'in_progress' | 'completed';
}

export interface TutorLessonProgressDTO {
  ok: boolean;
  grade: number;
  lesson_no: number;
  lesson_percent: number;
  status?: 'not_started' | 'in_progress' | 'completed';
  sublessons: TutorSublessonProgressDTO[];
  aggregation?: string;
}

export interface TutorLessonResumeDTO {
  ok: boolean;
  grade: number;
  lesson_no: number;
  sublesson_no: string;
  chunk_order_index: number;
  last_position?: unknown;
  from_saved_session?: boolean;
  /** Resumed sublesson textbook pages when provided (grades 2–3). */
  page_assets?: TutorPageAsset[];
}

export interface CanDoItem {
  id: string;
  statement_ta: string;
  statement_en: string;
  /**
   * Per–Can-Do token (lesson_no ≥ 1). Canonical set: `lesson_can_do_contract.py` /
   * `src/constants/lessonCanDoTypes.ts`. Older rows may omit or use non-canonical strings.
   */
  lesson_type?: string;
  rubric_levels?: { level: number; label: string; criteria: string }[];
  /** Set for Communication skills (lesson slot 0). */
  communication_topic_id?: string;
}

export interface CommunicationTopicMeta {
  id: string;
  title_en: string;
  title_ta?: string;
}

export interface CanDoCreatePayload {
  statement_ta: string;
  statement_en: string;
  /** Required for lesson_no ≥ 1; must be a token from `LESSON_CAN_DO_TYPES` (see `lessonCanDoTypes.ts`). */
  lesson_type?: string;
  rubric_levels?: { level: number; label: string; criteria: string }[];
  communication_topic_id?: string;
}

/** Student JWT-scoped lesson index when grade is unknown to the caller. */
const STUDENT_LESSON_INDEX_CACHE_GRADE = 0;

export type MalayTutorActivityKey =
  | 'malay_unit_5'
  | 'malay_jendela_seni_reading'
  | 'malay_narrative_learning_tutor';

export type MalayTutorActivityType =
  | 'lesson_teaching'
  | 'reading_comprehension'
  | 'narrative_learning';

/** Server-validated Malay activity context (Phase 1D). Authoritative over frontend registry. */
export interface MalayActivityContextResponse {
  ok: boolean;
  activity_key: MalayTutorActivityKey | string;
  activity_type: MalayTutorActivityType | string;
  runtime: 'ai_lesson_tutor' | string;
  display_title: string;
  category_label?: string;
  language_code: string;
  locale: string;
  dialect_key: string;
  feature_key: string;
  quota_key: string;
  grade: number;
  content_status: 'pending' | 'ready' | 'active' | 'validated' | string;
  content_version?: string | null;
  session_ready: boolean;
  tutor_display_name?: string;
  personas_available?: boolean;
  allowed_persona_keys?: string[];
  persona_selection_required?: boolean;
  simulation_disclosure?: string | null;
}

/** Unit 5 curriculum topic row from GET /v1/tutor/lesson-index?activity=malay_unit_5 */
export interface TutorLessonIndexTopic {
  id: string;
  section_id: string | null;
  title_ms: string;
  title_en: string;
  order: number;
  learning_objectives: string[];
  tutor_mode: string;
  is_selectable: boolean;
  activities?: Array<{
    id: string;
    title_ms: string;
    title_en: string;
    kind?: string;
  }>;
}

export interface TutorLessonIndexSection {
  id: string;
  title_ms: string;
  title_en: string;
  guiding_question_ms: string | null;
  order: number;
  topics: TutorLessonIndexTopic[];
}

export interface TutorActivityLessonIndexResponse {
  ok: boolean;
  activity_id: string;
  unit: {
    id: string;
    title_ms: string;
    title_en: string;
    core_question_ms?: string;
    source_file?: string;
  };
  default_topic_id: string;
  overview_topic: TutorLessonIndexTopic;
  last_visited_topic_id: string | null;
  sections: TutorLessonIndexSection[];
}

const malayActivityLessonIndexInflight = new Map<
  string,
  Promise<TutorActivityLessonIndexResponse>
>();

/** Test-only: clear activity lesson-index in-flight dedupe between Vitest cases. */
export function __resetMalayActivityLessonIndexInflightForTests(): void {
  malayActivityLessonIndexInflight.clear();
}

export async function getMalayActivityLessonIndex(
  activityKey: string,
  opts?: { signal?: AbortSignal }
): Promise<TutorActivityLessonIndexResponse> {
  const key = String(activityKey || '').trim();
  if (!key) {
    throw new Error('Activity key is required');
  }
  const inflightKey = `activity-lesson-index:${key}`;
  const existing = malayActivityLessonIndexInflight.get(inflightKey);
  if (existing) {
    return existing;
  }
  let run!: Promise<TutorActivityLessonIndexResponse>;
  run = (async (): Promise<TutorActivityLessonIndexResponse> => {
    try {
      const r = await client.get<TutorActivityLessonIndexResponse>(`${PREFIX}/lesson-index`, {
        params: { activity: key },
        signal: opts?.signal,
      });
      if (!r.data?.ok) {
        throw new Error('Could not load lesson contents.');
      }
      return r.data;
    } catch (e) {
      logApiErrorDiagnostics(e, 'GET /v1/tutor/lesson-index?activity=');
      throw new Error(getApiErrorMessage(e));
    } finally {
      if (malayActivityLessonIndexInflight.get(inflightKey) === run) {
        malayActivityLessonIndexInflight.delete(inflightKey);
      }
    }
  })();
  malayActivityLessonIndexInflight.set(inflightKey, run);
  return run;
}

export async function postTamilLessonSelection(body: {
  lesson_no: number;
  activity_id?: string;
}): Promise<{ ok: boolean; lesson_no: number; activity_id?: string }> {
  try {
    const r = await client.post<{ ok: boolean; lesson_no: number; activity_id?: string }>(
      `${PREFIX}/topic-selection`,
      body
    );
    return r.data;
  } catch (e) {
    logApiErrorDiagnostics(e, 'POST /v1/tutor/topic-selection (tamil lesson)');
    throw new Error(getApiErrorMessage(e));
  }
}

export async function postMalayTopicSelection(body: {
  activity: string;
  topic_id: string;
}): Promise<{ ok: boolean; topic_id: string; last_visited_topic_id?: string }> {
  try {
    const r = await client.post<{ ok: boolean; topic_id: string; last_visited_topic_id?: string }>(
      `${PREFIX}/topic-selection`,
      body
    );
    return r.data;
  } catch (e) {
    logApiErrorDiagnostics(e, 'POST /v1/tutor/topic-selection');
    throw new Error(getApiErrorMessage(e));
  }
}

/**
 * Resolve a Malay AI Lesson Tutor activity against trusted backend tenant/grade/eligibility.
 * Does not send tenant_id — gateway injects membership-scoped x-tenant-id.
 */
export async function getMalayActivityContext(
  activityKey: string,
  opts?: { signal?: AbortSignal }
): Promise<MalayActivityContextResponse> {
  const key = String(activityKey || '').trim();
  if (!key) {
    throw new Error('Activity key is required');
  }
  try {
    const r = await client.get<MalayActivityContextResponse>(`${PREFIX}/activity-context`, {
      params: { activity: key },
      signal: opts?.signal,
    });
    const data = r.data;
    if (!data || data.ok === false) {
      throw new Error('Could not resolve this learning activity.');
    }
    return data;
  } catch (e) {
    logApiErrorDiagnostics(e, 'GET /v1/tutor/activity-context');
    throw new Error(getApiErrorMessage(e));
  }
}

/** Public Reading progress payload (no internal state IDs). */
export interface MalayReadingProgressPayload {
  current_label: string;
  items: Array<{ key: string; label: string; status: string }>;
}

export interface MalayReadingTurnRequest {
  session_id: string;
  learner_text: string;
  client_event_id?: string;
  is_final?: boolean;
  is_partial?: boolean;
  draft_tutor_text?: string;
  content_version?: string;
}

export interface MalayReadingTurnResponse {
  ok: boolean;
  skipped?: boolean;
  reason?: string | null;
  controlled?: boolean;
  response_mode?: string | null;
  intent?: string | null;
  learner_turn_id?: string | null;
  tutor_turn_id?: string | null;
  normalized_learner_text?: string | null;
  transcript_quality?: string | null;
  resolved_claim?: string | null;
  final_tutor_text?: string | null;
  speak_prompt?: string | null;
  fallback_used?: boolean;
  fallback_id?: string | null;
  remediated?: boolean;
  reading_progress?: MalayReadingProgressPayload | null;
  lesson_stage?: string | null;
  advance_stage?: boolean;
  retrieval_performed?: boolean;
  prompt_version?: number | null;
  architecture?: string | null;
  persona_locked?: boolean;
  selected_persona?: string | null;
  selected_persona_display_name?: string | null;
  selected_persona_organisation?: string | null;
  simulation_disclosure?: string | null;
  persona_selection_failures?: number | null;
  show_a11y_persona_fallback?: boolean;
  error?: string | null;
}

/**
 * Reading-page JSON uses an integer (`1` for bucket `.../v1/`).
 * reading-turn stores package versions as `v1`, `v2`, …
 */
export function resolveMalayReadingTurnContentVersion(
  raw?: string | number | null,
): string | undefined {
  if (raw == null) return undefined;
  const s = String(raw).trim();
  if (!s) return undefined;
  if (/^\d+$/.test(s)) return `v${s}`;
  return s;
}

/**
 * Display text from POST /v1/tutor/reading-turn. Live UI injects `speak_prompt`
 * into Gemini; the student chat panel must use `final_tutor_text` first.
 */
export function extractMalayReadingTurnAssistantText(
  res: MalayReadingTurnResponse | null | undefined,
): string | null {
  if (!res || res.ok === false) return null;
  const finalText = String(res.final_tutor_text ?? '').trim();
  if (finalText) return finalText;
  const speak = String(res.speak_prompt ?? '').trim();
  if (speak) return speak;
  return null;
}

/**
 * Final Malay Reading learner-turn orchestration.
 * Fail-closed while Reading is not session_ready. Does not mint Gemini tokens.
 */
export async function postMalayReadingTurn(
  body: MalayReadingTurnRequest,
  opts?: { signal?: AbortSignal }
): Promise<MalayReadingTurnResponse> {
  const sessionId = String(body.session_id || '').trim();
  if (!sessionId) {
    throw new Error('Session is required');
  }
  try {
    const r = await client.post<MalayReadingTurnResponse>(`${PREFIX}/reading-turn`, {
      session_id: sessionId,
      learner_text: String(body.learner_text || ''),
      client_event_id: body.client_event_id,
      is_final: body.is_final !== false,
      is_partial: Boolean(body.is_partial),
      draft_tutor_text: body.draft_tutor_text,
      content_version: body.content_version,
    }, { signal: opts?.signal });
    return r.data;
  } catch (e) {
    logApiErrorDiagnostics(e, 'POST /v1/tutor/reading-turn');
    if (import.meta.env.DEV) {
      console.warn('[reading-turn] request failed', {
        status: isAxiosError(e) ? e.response?.status : undefined,
        code: getApiErrorCode(e),
        backendMessage: getApiErrorBackendMessage(e),
      });
    }
    throw new Error(getApiErrorMessage(e));
  }
}

export interface MalayNarrativeTurnRequest {
  session_id: string;
  learner_text: string;
  is_unclear?: boolean;
  client_event_id?: string;
  is_final?: boolean;
}

export interface MalayNarrativeTurnResponse {
  ok: boolean;
  skipped?: boolean;
  reason?: string | null;
  controlled?: boolean;
  intent?: string | null;
  advance_step?: boolean;
  tutor_text?: string | null;
  final_tutor_text?: string | null;
  plot_version?: number | null;
  plot_gate_step?: string | null;
  next_required_field?: string | null;
  pending_confirmation?: {
    field?: string | null;
    value?: string | null;
    kind?: string | null;
  } | null;
  workflow_stage?: string | null;
  session?: Record<string, unknown> | null;
  live_writing_context?: {
    draft_id?: string;
    draft_revision?: number;
    injection_text?: string;
    section_label?: string;
    plot_divergence_notes?: string[];
  } | null;
  error?: string | null;
}

export interface MalayNarrativeSessionEnsureRequest {
  session_id: string;
  help_session_id?: string | null;
  activity_key?: string;
  linked_draft_id?: string | null;
}

export interface MalayNarrativeSessionEnsureResponse {
  ok?: boolean;
  session_id?: string;
  [key: string]: unknown;
}

/**
 * Create or resume the authoritative Narrative learning session under a known opaque id
 * (typically the Live/tracking session id). Used after Start Class so turns + Writing Studio
 * handoff share the same backend session document.
 */
export async function ensureMalayNarrativeLearningSession(
  body: MalayNarrativeSessionEnsureRequest,
  opts?: { signal?: AbortSignal }
): Promise<{ session_id: string }> {
  const sessionId = String(body.session_id || '').trim();
  if (!sessionId) {
    throw new Error('Session is required');
  }
  try {
    const r = await client.post<MalayNarrativeSessionEnsureResponse>(
      `${PREFIX}/narrative-writing/sessions`,
      {
        activity_key: body.activity_key || 'malay_narrative_learning_tutor',
        session_id: sessionId,
        help_session_id: body.help_session_id || undefined,
        linked_draft_id: body.linked_draft_id || undefined,
      },
      { signal: opts?.signal }
    );
    const out = String(r.data?.session_id || sessionId).trim();
    if (!out) {
      throw new Error('Narrative learning session id missing from response');
    }
    return { session_id: out };
  } catch (e) {
    logApiErrorDiagnostics(e, 'POST /v1/tutor/narrative-writing/sessions');
    throw new Error(getApiErrorMessage(e));
  }
}

/**
 * Final Malay Narrative Learning learner-turn orchestration (Phase 2D smoke fixes).
 * Best-effort from Live UI; does not mint Gemini tokens.
 */
/** Keep Continue/handoff responsive — never let a hung plot-turn block the Live UI. */
const NARRATIVE_TURN_CLIENT_TIMEOUT_MS = 12_000;

export async function postMalayNarrativeTurn(
  body: MalayNarrativeTurnRequest,
  opts?: { signal?: AbortSignal }
): Promise<MalayNarrativeTurnResponse> {
  const sessionId = String(body.session_id || '').trim();
  if (!sessionId) {
    throw new Error('Session is required');
  }
  const timeoutAc = new AbortController();
  const timeoutId = globalThis.setTimeout(
    () => timeoutAc.abort(),
    NARRATIVE_TURN_CLIENT_TIMEOUT_MS,
  );
  const onOuterAbort = () => timeoutAc.abort();
  if (opts?.signal) {
    if (opts.signal.aborted) timeoutAc.abort();
    else opts.signal.addEventListener('abort', onOuterAbort, { once: true });
  }
  try {
    const r = await client.post<MalayNarrativeTurnResponse>(
      `${PREFIX}/narrative-writing/sessions/${encodeURIComponent(sessionId)}/turns`,
      {
        learner_text: String(body.learner_text || ''),
        is_unclear: Boolean(body.is_unclear),
        client_event_id: body.client_event_id,
        is_final: body.is_final !== false,
      },
      { signal: timeoutAc.signal, timeout: NARRATIVE_TURN_CLIENT_TIMEOUT_MS }
    );
    return r.data;
  } catch (e) {
    logApiErrorDiagnostics(e, 'POST /v1/tutor/narrative-writing/sessions/{id}/turns');
    throw new Error(getApiErrorMessage(e));
  } finally {
    globalThis.clearTimeout(timeoutId);
    opts?.signal?.removeEventListener('abort', onOuterAbort);
  }
}

export const listLessons = async (opts?: { bypassCache?: boolean }): Promise<LessonItem[]> => {
  const tenantScope = tutorCacheTenantScope();
  if (opts?.bypassCache) {
    invalidateLessonIndexCache(STUDENT_LESSON_INDEX_CACHE_GRADE, tenantScope);
  } else {
    const cached = getCachedLessonIndex(STUDENT_LESSON_INDEX_CACHE_GRADE, tenantScope);
    if (cached) return cached;
  }

  try {
    const r = await client.get<{
      ok: boolean;
      lessons: LessonItem[];
      fallback_reason?: string;
    }>(`${PREFIX}/lesson-index`);
    const data = r.data?.lessons ?? [];
    const fallbackReason = String(r.data?.fallback_reason ?? '').trim();
    if (fallbackReason) {
      throw new Error(
        'Could not load your lesson list right now. Please wait a moment and tap Try again.'
      );
    }
    if (data.length > 0) {
      setCachedLessonIndex(STUDENT_LESSON_INDEX_CACHE_GRADE, data, tenantScope);
    }
    return data;
  } catch (e) {
    logApiErrorDiagnostics(e, 'GET /v1/tutor/lesson-index');
    throw new Error(getApiErrorMessage(e));
  }
};

/** Teacher-only: lessons for a specific grade (roster student /lesson-index is not usable for teachers). */
export const listTeacherLessons = async (grade: number): Promise<LessonItem[]> => {
  const r = await client.get<{ ok: boolean; lessons: LessonItem[] }>(`${PREFIX}/teacher/lesson-index`, {
    params: { grade },
  });
  return r.data?.lessons ?? [];
};

export interface StoryItem {
  lesson_no: number;
  lesson_title: string;
  video_url: string;
  thumbnail_url: string | null;
}

/** Response from `GET /v1/tutor/communication-instruction` (topic + dialect resolved prompt). */
export interface CommunicationInstructionResponse {
  ok?: boolean;
  lesson_title?: string;
  lesson_style?: string;
  lesson_type?: string;
  dialect_key?: string;
  systemInstruction?: string;
  topic_id?: string;
  topic_title?: string;
  fallback_reason?: string;
  error?: string;
  message?: string;
}

function communicationInstructionDedupeKey(params: {
  topicId?: string;
  dialectKey: string;
  topicLabel?: string;
}): string {
  const dialectKey = String(params.dialectKey || '').trim();
  const tid = String(params.topicId || '').trim();
  const label = String(params.topicLabel || '').trim();
  return `${tid || label}:${dialectKey}`;
}

const communicationInstructionInFlight = new Map<
  string,
  Promise<CommunicationInstructionResponse>
>();
const communicationInstructionFailedKeys = new Set<string>();

const COMMUNICATION_INSTRUCTION_RETRY_DELAY_MS = 1000;

function sleepMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isCancelledCommunicationInstructionRequest(error: unknown): boolean {
  if (isAxiosError(error) && error.code === 'ERR_CANCELED') return true;
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  if (error instanceof Error && error.name === 'AbortError') return true;
  return false;
}

/** Transient failures eligible for a single automatic retry on communication-instruction only. */
function isRetryableCommunicationInstructionError(
  error: unknown,
  signal?: AbortSignal
): boolean {
  if (signal?.aborted) return false;
  if (isCancelledCommunicationInstructionRequest(error)) return false;
  if (isTemporaryTutorNetworkError(error)) return true;
  const msg = String((error as { message?: string })?.message || error).toLowerCase();
  return msg.includes('readtimeout');
}

async function fetchCommunicationInstructionOnce(
  query: Record<string, string>,
  cacheKey: string | null | undefined,
  signal?: AbortSignal
): Promise<CommunicationInstructionResponse> {
  const r = await gatewayTutorClient.get<CommunicationInstructionResponse>(
    `${GATEWAY_TUTOR_PREFIX}/communication-instruction`,
    { params: query, signal }
  );
  const data = r.data ?? {};
  if (data.ok === false) {
    const msg =
      (typeof data.error === 'string' && data.error.trim()) ||
      (typeof data.message === 'string' && data.message.trim()) ||
      'Communication instruction request was not successful.';
    throw new Error(msg);
  }
  const instructionText = String(data.systemInstruction || '').trim();
  if (instructionText && cacheKey) {
    setCachedSystemInstruction(cacheKey, instructionText);
  }
  return data;
}

async function requestCommunicationInstructionWithRetry(
  query: Record<string, string>,
  cacheKey: string | null | undefined,
  signal?: AbortSignal
): Promise<CommunicationInstructionResponse> {
  const devLog = import.meta.env.DEV
    ? (message: string) => console.log(message)
    : () => {};

  devLog('[CommunicationInstruction] Initial request');
  try {
    return await fetchCommunicationInstructionOnce(query, cacheKey, signal);
  } catch (initialError) {
    if (!isRetryableCommunicationInstructionError(initialError, signal)) {
      throw initialError;
    }
    devLog('[CommunicationInstruction] Retry #1 after transient failure');
    await sleepMs(COMMUNICATION_INSTRUCTION_RETRY_DELAY_MS);
    if (signal?.aborted) throw initialError;
    try {
      const data = await fetchCommunicationInstructionOnce(query, cacheKey, signal);
      devLog('[CommunicationInstruction] Retry succeeded');
      return data;
    } catch {
      devLog('[CommunicationInstruction] Retry failed');
      throw initialError;
    }
  }
}

/** Clears failed dedupe for one key or all keys (manual retry / topic change). */
export function clearCommunicationInstructionDedupe(key?: string): void {
  if (key) {
    communicationInstructionFailedKeys.delete(key);
    communicationInstructionInFlight.delete(key);
    return;
  }
  communicationInstructionFailedKeys.clear();
  communicationInstructionInFlight.clear();
}

/**
 * Loads the Communication Tutor system instruction.
 * Primary path: `topic_id` + `dialect_key`. Optional `topic_label` for legacy backends only.
 */
export async function getCommunicationInstruction(
  params: {
    topicId?: string;
    dialectKey: string;
    /** Legacy fallback when `topic_id` is unavailable */
    topicLabel?: string;
    /** Topic title from the sidebar — used when topic_id lookup returns 502 */
    fallbackTopicLabel?: string;
    fallbackTopicDescription?: string;
  },
  opts?: { signal?: AbortSignal; force?: boolean; labelOnlyFallback?: boolean }
): Promise<CommunicationInstructionResponse> {
  const dialectKey = String(params.dialectKey || '').trim();
  if (!dialectKey) throw new Error('dialect_key is required.');

  const tid = String(params.topicId || '').trim();
  const label = String(params.topicLabel || '').trim();
  if (!tid && !label) {
    throw new Error('Missing topic: provide topic_id or topic_label.');
  }

  const dedupeKey = communicationInstructionDedupeKey(params);
  const cacheKey = buildSystemInstructionCacheKey({
    grade: 0,
    lessonNo: 0,
    sublesson: 0,
    dialectKey,
    topicKey: tid || label,
    tenantScope: tutorCacheTenantScope(),
  });

  if (!opts?.force && cacheKey) {
    const cachedInstruction = getCachedSystemInstruction(cacheKey);
    if (cachedInstruction) {
      return {
        ok: true,
        systemInstruction: cachedInstruction,
        dialect_key: dialectKey,
        ...(tid ? { topic_id: tid } : {}),
        ...(label ? { topic_title: label } : {}),
      };
    }
  }

  if (!opts?.force) {
    if (communicationInstructionFailedKeys.has(dedupeKey)) {
      throw new Error(
        'Communication Tutor service is temporarily unavailable. Please try again.'
      );
    }
    const pending = communicationInstructionInFlight.get(dedupeKey);
    if (pending) return pending;
  } else {
    communicationInstructionFailedKeys.delete(dedupeKey);
    communicationInstructionInFlight.delete(dedupeKey);
  }

  const query: Record<string, string> = { dialect_key: dialectKey };
  if (tid) {
    query.topic_id = tid;
  } else {
    query.topic_label = label;
  }

  const run = (async (): Promise<CommunicationInstructionResponse> => {
    try {
      return await requestCommunicationInstructionWithRetry(query, cacheKey, opts?.signal);
    } catch (e: unknown) {
      if (isAxiosError(e)) {
        const status = e.response?.status;
        const data = e.response?.data;
        const bodyText =
          typeof data === 'string' ? data : JSON.stringify(data ?? {});
        if (status === 404) {
          throw new Error(`Topic not found (404). ${bodyText}`.trim());
        }
        if (status === 502) {
          const fallbackLabel = String(
            params.fallbackTopicLabel || params.topicLabel || ''
          ).trim();
          if (tid && fallbackLabel && !opts?.labelOnlyFallback) {
            try {
              return await getCommunicationInstruction(
                {
                  dialectKey,
                  topicLabel: fallbackLabel,
                  fallbackTopicLabel: fallbackLabel,
                  fallbackTopicDescription: params.fallbackTopicDescription,
                },
                { signal: opts?.signal, force: true, labelOnlyFallback: true }
              );
            } catch {
              /* try local fallback below */
            }
          }
          if (fallbackLabel) {
            const instructionText = buildCommunicationInstructionFallback({
              topicTitle: fallbackLabel,
              topicDescription: params.fallbackTopicDescription,
              dialectKey,
            });
            if (cacheKey) {
              setCachedSystemInstruction(cacheKey, instructionText);
            }
            return {
              ok: true,
              systemInstruction: instructionText,
              lesson_title: fallbackLabel,
              topic_title: fallbackLabel,
              lesson_style: 'CONVERSATION',
              lesson_type: 'topic',
              dialect_key: dialectKey,
              ...(tid ? { topic_id: tid } : {}),
              fallback_reason: 'communication_instruction_502',
            };
          }
        }
        throw new Error(
          `Communication instruction failed: ${status ?? 'network'} ${bodyText}`.trim()
        );
      }
      if (isTemporaryTutorNetworkError(e)) {
        communicationInstructionFailedKeys.add(dedupeKey);
      }
      throw e;
    } finally {
      communicationInstructionInFlight.delete(dedupeKey);
    }
  })();

  communicationInstructionInFlight.set(dedupeKey, run);
  return run;
}

export const listStories = async (grade: number = 2): Promise<StoryItem[]> => {
  const r = await client.get<{ ok: boolean; grade: number; stories: StoryItem[] }>(
    `${PREFIX}/stories`,
    { params: { grade } }
  );
  return r.data?.stories ?? [];
};

export const listCanDos = async (grade: number, lessonNo: number): Promise<CanDoItem[]> => {
  const r = await client.get<{ ok: boolean; can_dos: CanDoItem[] }>(
    `${PREFIX}/lessons/${grade}/${lessonNo}/can-dos`
  );
  return r.data?.can_dos ?? [];
};

/** Lesson 0 = Communication Tutor skills (topics + can-dos). */
export const fetchCanDosBundle = async (
  grade: number,
  lessonNo: number,
  opts?: { signal?: AbortSignal }
): Promise<{ can_dos: CanDoItem[]; communication_topics: CommunicationTopicMeta[] }> => {
  const r = await client.get<{
    ok: boolean;
    can_dos: CanDoItem[];
    communication_topics?: CommunicationTopicMeta[];
  }>(`${PREFIX}/lessons/${grade}/${lessonNo}/can-dos`, { signal: opts?.signal });
  return {
    can_dos: r.data?.can_dos ?? [],
    communication_topics: r.data?.communication_topics ?? [],
  };
};

export const createCommunicationTopic = async (
  grade: number,
  payload: { title_en: string; title_ta?: string }
): Promise<CommunicationTopicMeta> => {
  const r = await client.post<{ ok: boolean; topic: CommunicationTopicMeta }>(
    `${PREFIX}/lessons/${grade}/0/communication-topics`,
    payload
  );
  return r.data.topic;
};

export const updateCommunicationTopic = async (
  grade: number,
  topicId: string,
  payload: { title_en?: string; title_ta?: string }
): Promise<CommunicationTopicMeta> => {
  const r = await client.put<{ ok: boolean; topic: CommunicationTopicMeta }>(
    `${PREFIX}/lessons/${grade}/0/communication-topics/${encodeURIComponent(topicId)}`,
    payload
  );
  return r.data.topic;
};

export const deleteCommunicationTopic = async (grade: number, topicId: string): Promise<void> => {
  await client.delete(
    `${PREFIX}/lessons/${grade}/0/communication-topics/${encodeURIComponent(topicId)}`
  );
};

export const createCanDo = async (
  grade: number,
  lessonNo: number,
  payload: CanDoCreatePayload
): Promise<CanDoItem> => {
  const body =
    lessonNo >= 1 && payload.lesson_type != null
      ? { ...payload, lesson_type: normalizeCanDoLessonTypeField(lessonNo, payload.lesson_type) ?? payload.lesson_type }
      : { ...payload };
  const r = await client.post<{ ok: boolean; can_do: CanDoItem }>(
    `${PREFIX}/lessons/${grade}/${lessonNo}/can-dos`,
    body
  );
  return r.data.can_do;
};

export const updateCanDo = async (
  grade: number,
  lessonNo: number,
  canDoId: string,
  payload: Partial<CanDoCreatePayload>
): Promise<CanDoItem> => {
  const body =
    lessonNo >= 1 && payload.lesson_type != null
      ? { ...payload, lesson_type: normalizeCanDoLessonTypeField(lessonNo, payload.lesson_type) ?? payload.lesson_type }
      : { ...payload };
  const r = await client.put<{ ok: boolean; can_do: CanDoItem }>(
    `${PREFIX}/lessons/${grade}/${lessonNo}/can-dos/${canDoId}`,
    body
  );
  return r.data.can_do;
};

export const deleteCanDo = async (
  grade: number,
  lessonNo: number,
  canDoId: string
): Promise<void> => {
  await client.delete(`${PREFIX}/lessons/${grade}/${lessonNo}/can-dos/${canDoId}`);
};

export type CommunicationCanDoFocusArea = 'listening' | 'speaking' | 'reading' | 'writing';

export type CommunicationCanDoGeneratePayload = {
  communication_topic_id: string;
  focus_areas?: CommunicationCanDoFocusArea[];
  statements_count?: number;
};

export const generateCommunicationCanDos = async (
  grade: number,
  payload: CommunicationCanDoGeneratePayload
): Promise<CanDoItem[]> => {
  const r = await client.post<{ ok: boolean; can_dos: CanDoItem[]; preview?: boolean }>(
    `${PREFIX}/lessons/${grade}/0/communication-can-dos/generate`,
    payload
  );
  return r.data?.can_dos ?? [];
};

export const generateCanDos = async (
  grade: number,
  lessonNo: number,
  lessonType: string,
  options?: { persist?: boolean }
): Promise<CanDoItem[]> => {
  const trimmed = lessonType.trim();
  const normalized = trimmed.toLowerCase();
  const persist = options?.persist ?? false;
  const r = await client.post<{ ok: boolean; can_dos: CanDoItem[]; persisted?: boolean }>(
    `${PREFIX}/lessons/${grade}/${lessonNo}/can-dos/generate`,
    { lesson_type: trimmed, persist }
  );
  const rows = r.data?.can_dos ?? [];
  return rows.map((cd) => {
    const fromServer = (cd.lesson_type && String(cd.lesson_type).trim().toLowerCase()) || '';
    return { ...cd, lesson_type: fromServer || normalized };
  });
};

// --- Student Progress (Grade 2 students) ---

export interface LessonProgressItem {
  lesson_no: number;
  last_session_at: string | null;
  last_checkpoint: string | null;
  overall_score: number;
  can_do_scores: { can_do_id: string; score: number; assessed_at: string }[];
  sessions_count: number;
}

export interface StudentContext {
  last_lesson_no: number | null;
  last_lesson_title: string | null;
  last_session_at: string | null;
  current_lesson_no: number;
  last_checkpoint: string | null;
  summary?: string;
  next_steps?: string[];
}

export const getStudentContext = async (lessonNo: number): Promise<StudentContext> => {
  const r = await client.get<{
    ok: boolean;
    last_lesson_no: number | null;
    last_lesson_title: string | null;
    last_session_at: string | null;
    current_lesson_no: number;
    last_checkpoint: string | null;
    summary?: string;
    next_steps?: string[];
  }>(`${PREFIX}/students/me/context`, { params: { lesson_no: lessonNo } });
  return {
    last_lesson_no: r.data?.last_lesson_no ?? null,
    last_lesson_title: r.data?.last_lesson_title ?? null,
    last_session_at: r.data?.last_session_at ?? null,
    current_lesson_no: r.data?.current_lesson_no ?? lessonNo,
    last_checkpoint: r.data?.last_checkpoint ?? null,
    summary: r.data?.summary,
    next_steps: r.data?.next_steps,
  };
};

export const resetProgress = async (lessonNo: number): Promise<void> => {
  await client.post<{ ok: boolean; lesson_no: number }>(
    `${PREFIX}/students/me/progress/reset`,
    { lesson_no: lessonNo }
  );
};

export const getStudentProgress = async (): Promise<LessonProgressItem[]> => {
  const r = await client.get<{ ok: boolean; progress: LessonProgressItem[] }>(
    `${PREFIX}/students/me/progress`
  );
  return r.data?.progress ?? [];
};

/** Mongo ``student_progress`` analytics (strengths / weaknesses / multi-attempt trend). */
export type TrendPointOut = {
  at?: string | null;
  overall_score?: number | string | null;
  session_id?: string | null;
};

export type LessonProgressSummaryOut = {
  lesson_no?: number | null;
  overall_score?: number | string | null;
  sessions_count?: number | null;
  updated_at?: string | null;
  mastery_by_can_do?: Record<string, string>;
  attempt_history?: unknown[];
};

export type StudentProgressAnalyticsOut = {
  tenant_id: string;
  student_id: string;
  grade: number;
  strengths: Array<Record<string, unknown>>;
  weaknesses: Array<Record<string, unknown>>;
  recommendations: string[];
  lessons: LessonProgressSummaryOut[];
  score_trend: TrendPointOut[];
};

export type ClassProgressAnalyticsOut = {
  tenant_id: string;
  grade: number;
  student_ids_filter: string[];
  per_student: Array<Record<string, unknown>>;
  mastery_by_can_do: Array<Record<string, unknown>>;
  weak_skills: Array<Record<string, unknown>>;
  class_insights: {
    weak_can_do_ids: string[];
    lowest_avg_score: number | null;
    students_below_threshold: number;
  };
};

export async function getStudentProgressAnalytics(grade?: number): Promise<StudentProgressAnalyticsOut | null> {
  try {
    const r = await client.get<StudentProgressAnalyticsOut>(`${PREFIX}/progress/analytics/student`, {
      params: grade != null ? { grade } : undefined,
    });
    return r.data ?? null;
  } catch (e) {
    if (isAxiosError(e) && [400, 403, 404].includes(Number(e.response?.status))) {
      return null;
    }
    throw e;
  }
}

/** Teacher class rollups from ``student_progress`` (requires roster ``platform_user_id`` ids). */
export async function getClassProgressAnalytics(
  grade: number,
  studentIds: string[],
  opts?: { lessonNo?: number; lessonType?: string }
): Promise<ClassProgressAnalyticsOut | null> {
  const ids = [...new Set(studentIds.map((x) => String(x).trim()).filter(Boolean))].slice(0, 200);
  if (!ids.length) return null;
  /**
   * Always call the **gateway** path `/v1/tutor/progress/analytics/class` (same contract as
   * `teacherTutorAnalyticsApi`). Using `TUTOR_BASE` + `tutorRestApiPrefix()` here caused
   * `GET …/tutor/progress/...` against the gateway when `VITE_AI_TUTOR_BASE_URL` pointed at
   * direct ai-tutor — the BFF only forwards `/v1/tutor/*` → **404**.
   */
  const gatewayClient = createHttpClient(normalizeTutorBaseUrl(serviceUrls.gateway));
  try {
    const params: Record<string, string | number> = { grade, student_ids: ids.join(',') };
    if (opts?.lessonNo != null && Number.isFinite(opts.lessonNo)) {
      params.lesson_no = opts.lessonNo;
    }
    const lt = opts?.lessonType?.trim().toLowerCase();
    if (lt) params.lesson_type = lt;
    const r = await gatewayClient.get<ClassProgressAnalyticsOut>(`/v1/tutor/progress/analytics/class`, {
      params,
    });
    return r.data ?? null;
  } catch (e) {
    if (isAxiosError(e) && [400, 403, 404, 500, 502, 503].includes(Number(e.response?.status))) {
      return null;
    }
    throw e;
  }
}

// --- Teacher: student progress and session history ---

export interface TutorSessionItem {
  session_id: string;
  lesson_no: number;
  started_at: string | null;
  ended_at: string | null;
  duration_seconds: number;
  checkpoint_at_end: string | null;
  rubric_scores: Record<string, number>;
  status: string;
}

export const getTeacherStudentProgress = async (
  studentId: string
): Promise<LessonProgressItem[]> => {
  const r = await client.get<{ ok: boolean; progress: LessonProgressItem[] }>(
    `${PREFIX}/teacher/students/${studentId}/progress`
  );
  return r.data?.progress ?? [];
};

export const getTeacherStudentSessions = async (
  studentId: string,
  limit?: number
): Promise<TutorSessionItem[]> => {
  const r = await client.get<{ ok: boolean; sessions: TutorSessionItem[] }>(
    `${PREFIX}/teacher/students/${studentId}/sessions`,
    { params: limit != null ? { limit } : undefined }
  );
  return r.data?.sessions ?? [];
};

// --- Lesson chunk progress + resume (Mongo) ---

export const getTutorLessonProgress = async (
  lessonNo: number,
  grade?: number
): Promise<TutorLessonProgressDTO> => {
  const r = await client.get<TutorLessonProgressDTO>(`${PREFIX}/progress/lesson/${lessonNo}`, {
    params: grade != null ? { grade } : undefined,
  });
  return r.data;
};

/** One request for many lessons (dashboard); caps at 40 lesson numbers server-side. */
export const postTutorProgressBatch = async (
  lessonNos: number[],
  grade?: number
): Promise<TutorLessonProgressDTO[]> => {
  if (!lessonNos?.length) return [];
  const r = await client.post<{ ok: boolean; lessons: TutorLessonProgressDTO[] }>(
    `${PREFIX}/progress/batch`,
    { lesson_nos: lessonNos },
    { params: grade != null ? { grade } : undefined }
  );
  return r.data?.lessons ?? [];
};

export const getTutorLessonResume = async (
  lessonNo: number,
  grade?: number
): Promise<TutorLessonResumeDTO> => {
  const r = await client.get<TutorLessonResumeDTO>(`${PREFIX}/resume/${lessonNo}`, {
    params: grade != null ? { grade } : undefined,
  });
  return r.data;
};

export const postTutorProgressUpdate = async (
  payload: {
    lesson_no: number;
    sublesson_no: string;
    chunk_order_index: number;
    interaction_done?: boolean;
    time_spent_sec?: number;
  },
  grade?: number,
  opts?: { signal?: AbortSignal }
): Promise<{ ok: boolean; status: string; time_spent_sec: number; interaction_done: boolean }> => {
  const r = await client.post(`${PREFIX}/progress/update`, payload, {
    params: grade != null ? { grade } : undefined,
    signal: opts?.signal,
  });
  return r.data;
};

export const postTutorSessionPosition = async (
  payload: {
    lesson_no: number;
    sublesson_no: string;
    chunk_order_index: number;
    last_position?: unknown;
  },
  grade?: number
): Promise<void> => {
  const chunkIdx = payload.chunk_order_index;
  if (!Number.isFinite(chunkIdx) || chunkIdx < 0) {
    console.warn("[TKM tutor] skipped session/update: invalid chunk_order_index", {
      chunk_order_index: chunkIdx,
      lesson_no: payload.lesson_no,
      sublesson_no: payload.sublesson_no,
    });
    return;
  }
  try {
    await client.post(`${PREFIX}/session/update`, payload, {
      params: grade != null ? { grade } : undefined,
    });
  } catch (e) {
    const detail = isAxiosError(e)
      ? `${e.response?.status} ${JSON.stringify(e.response?.data ?? e.message)}`
      : String(e);
    console.warn(
      "[TKM tutor] POST session/update failed (does not affect Live WebSocket; check chunk_order_index / server rules):",
      detail,
      { payload, grade }
    );
    throw e;
  }
};
